








import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  updateUserApi,
  resetUserPasswordApi,
  getDepartmentsApi,
} from "../../app/api";
import { toast } from "react-toastify";

/* ======================================================
   SUCCESS / FAIL MODAL (UNCHANGED)
====================================================== */
function UpdateResponseModal({ show, success, message, onClose }) {
  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-[3000]">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-[320px] text-center relative">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${success ? "bg-green-500" : "bg-red-500"
              }`}
          >
            <span className="text-white text-4xl">
              {success ? "✓" : "✕"}
            </span>
          </div>
        </div>

        <div className="mt-14">
          <h2
            className={`text-xl font-semibold ${success ? "text-green-600" : "text-red-600"
              }`}
          >
            {success ? "Success" : "Failed"}
          </h2>

          <p className="text-gray-600 mt-2 mb-6">{message}</p>

          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ======================================================
   EDIT USER - GLASSMORPHISM VERSION
====================================================== */
export default function EditUser({ user, onClose, onUpdated }) {
  const [form, setForm] = useState({
    phone_number: user.phone_number || "",
    alter_contact_name: user.alter_contact_name || "",
    alter_number: user.alter_number || "",
    department_id: user.department_id || "",
    job_title: user.job_title || "",
    dob: user.dob || "",
    address: user.address || "",
    biometric_id: user.biometric_id || "",
    gender: user.gender || "",
  });

  const [departments, setDepartments] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const [updateModal, setUpdateModal] = useState({
    show: false,
    success: false,
    message: "",
  });

  useEffect(() => {
    getDepartmentsApi().then((res) => {
      if (res.success) setDepartments(res.data);
    });
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleUpdate = async (e) => {
    e.preventDefault();
    const res = await updateUserApi(user.id, form);

    if (res.success) {
      setUpdateModal({
        show: true,
        success: true,
        message: "Employee updated successfully",
      });
      onUpdated();
    } else {
      setUpdateModal({
        show: true,
        success: false,
        message: res.message || "Update failed",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) return toast.error("Enter password");

    const res = await resetUserPasswordApi(user.id, newPassword);
    if (res.success) {
      toast.success("Password reset successfully");
      setShowResetModal(false);
      setNewPassword("");
    } else {
      toast.error(res.message || "Failed");
    }
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-[2000]" />

      {/* Modal */}
      <div className="fixed inset-0 z-[2001] flex justify-center items-start overflow-y-auto px-4 py-10">
        <div className="relative w-full max-w-3xl rounded-2xl p-8 bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl text-white">

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 transition"
          >
            ✕
          </button>

          <h3 className="text-xl font-semibold mb-6">
            Edit Employee
          </h3>

          <form
            onSubmit={handleUpdate}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-1"
          >
            {/* Read Only */}
            <Input label="Employee ID" value={user.emp_id} disabled />
            <Input label="Email" value={user.email} disabled />
            <Input
              label="Full Name"
              value={`${user.first_name} ${user.last_name}`}
              disabled
            />

            {/* Editable */}
            <Input
              label="Phone"
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
            />

            <Input
              label="Alternate Contact Name"
              name="alter_contact_name"
              value={form.alter_contact_name}
              onChange={handleChange}
            />

            <Input
              label="Alternate Contact Number"
              name="alter_number"
              value={form.alter_number}
              onChange={handleChange}
            />

            <Select
              label="Department"
              name="department_id"
              value={form.department_id}
              onChange={handleChange}
            >




              <option value="" className="text-white bg-blue-600">Select Department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id} className="text-black bg-white">
                  {d.name}
                </option>
              ))}
            </Select>

            <Input
              label="Job Title"
              name="job_title"
              value={form.job_title}
              onChange={handleChange}
            />

            <Input
              type="date"
              label="DOB"
              name="dob"
              value={form.dob}
              onChange={handleChange}
            />

            <Input
              label="Biometric ID"
              name="biometric_id"
              value={form.biometric_id}
              onChange={handleChange}
            />

            <Select
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
            >
              <option value="" className="text-white bg-blue-600">Select Gender</option>
              <option value="male" className="text-black bg-white">Male</option>
              <option value="female" className="text-black bg-white">Female</option>
            </Select>

            <Textarea
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
            />

            {/* Buttons */}
            <div className="md:col-span-2 flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="px-6 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition"
              >
                Reset Password
              </button>

              <button
                type="submit"
                className="px-6 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 transition"
              >
                Update Employee
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Reset Password Modal (Glass Styled) */}
      {showResetModal &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-[3000] flex items-center justify-center">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 w-[320px] text-white">
              <h3 className="font-semibold mb-4">New Password</h3>
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white mb-4 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <UpdateResponseModal
        show={updateModal.show}
        success={updateModal.success}
        message={updateModal.message}
        onClose={() => {
          setUpdateModal({ show: false, success: false, message: "" });
          if (updateModal.success) onClose();
        }}
      />
    </>,
    document.body
  );
}

/* ================= HELPERS ================= */

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <input
        {...props}
        className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
      />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <select
        {...props}
        className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-purple-400/50"
      >
        {children}
      </select>
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div className="md:col-span-2 flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <textarea
        {...props}
        rows={3}
        className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white resize-none focus:outline-none focus:ring-2 focus:ring-purple-400/50"
      />
    </div>
  );
}







// import { useState, useEffect } from "react";
// import { createPortal } from "react-dom";
// import {
//   updateUserApi,
//   resetUserPasswordApi,
//   getDepartmentsApi,
// } from "../../app/api";
// import { toast } from "react-toastify";

// /* ======================================================
//    SUCCESS / FAIL MODAL (UNCHANGED)
// ====================================================== */
// function UpdateResponseModal({ show, success, message, onClose }) {
//   if (!show) return null;

//   return createPortal(
//     <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[3000]">
//       <div className="bg-white p-6 rounded-2xl shadow-2xl w-[320px] text-center relative">
//         <div className="absolute -top-10 left-1/2 -translate-x-1/2">
//           <div
//             className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${
//               success ? "bg-green-500" : "bg-red-500"
//             }`}
//           >
//             <span className="text-white text-4xl">
//               {success ? "✓" : "✕"}
//             </span>
//           </div>
//         </div>

//         <div className="mt-14">
//           <h2
//             className={`text-xl font-semibold ${
//               success ? "text-green-600" : "text-red-600"
//             }`}
//           >
//             {success ? "Success" : "Failed"}
//           </h2>

//           <p className="text-gray-600 mt-2 mb-6">{message}</p>

//           <button
//             onClick={onClose}
//             className="w-full py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
//           >
//             OK
//           </button>
//         </div>
//       </div>
//     </div>,
//     document.body
//   );
// }

// /* ======================================================
//    EDIT USER (PORTAL + NEW UI)
// ====================================================== */
// export default function EditUser({ user, onClose, onUpdated }) {
//   const [form, setForm] = useState({
//     phone_number: user.phone_number || "",
//     alter_contact_name: user.alter_contact_name || "",
//     alter_number: user.alter_number || "",
//     department_id: user.department_id || "",
//     job_title: user.job_title || "",
//     dob: user.dob || "",
//     address: user.address || "",
//     biometric_id: user.biometric_id || "",
//     gender: user.gender || "",
//   });

//   const [departments, setDepartments] = useState([]);
//   const [showResetModal, setShowResetModal] = useState(false);
//   const [newPassword, setNewPassword] = useState("");

//   const [updateModal, setUpdateModal] = useState({
//     show: false,
//     success: false,
//     message: "",
//   });

//   useEffect(() => {
//     getDepartmentsApi().then((res) => {
//       if (res.success) setDepartments(res.data);
//     });
//   }, []);

//   const handleChange = (e) =>
//     setForm({ ...form, [e.target.name]: e.target.value });

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     const res = await updateUserApi(user.id, form);

//     if (res.success) {
//       setUpdateModal({
//         show: true,
//         success: true,
//         message: "Employee updated successfully",
//       });
//       onUpdated();
//     } else {
//       setUpdateModal({
//         show: true,
//         success: false,
//         message: res.message || "Update failed",
//       });
//     }
//   };

//   const handleResetPassword = async () => {
//     if (!newPassword.trim()) return toast.error("Enter password");

//     const res = await resetUserPasswordApi(user.id, newPassword);
//     if (res.success) {
//       toast.success("Password reset successfully");
//       setShowResetModal(false);
//       setNewPassword("");
//     } else {
//       toast.error(res.message || "Failed");
//     }
//   };

//   /* ================== PORTAL RENDER ================== */
//   return createPortal(
//     <>
//       {/* OVERLAY */}
//       <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[2000]" />

//       {/* MODAL */}
//       <div className="fixed inset-0 z-[2001] flex justify-center items-start overflow-y-auto px-4 py-10">
//         <div
//           className="
//             relative w-full max-w-xl
//             rounded-3xl p-6
//             bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f]
//             text-white shadow-2xl
//           "
//         >
//           {/* CLOSE */}
//           <button
//             onClick={onClose}
//             className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 transition"
//           >
//             ✕
//           </button>

//           <h3 className="text-2xl font-semibold text-center mb-6">
//             Edit Employee
//           </h3>

//           <form
//             onSubmit={handleUpdate}
//             className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
//           >
//             {/* READ ONLY */}
//             <Input label="Employee ID" value={user.emp_id} disabled />
//             <Input label="Email" value={user.email} disabled />
//             <Input
//               label="Full Name"
//               value={`${user.first_name} ${user.last_name}`}
//               disabled
//             />

//             {/* EDIT */}
//             <Input
//               label="Phone"
//               name="phone_number"
//               value={form.phone_number}
//               onChange={handleChange}
//             />

//             <Input
//               label="Alternate Contact Name"
//               name="alter_contact_name"
//               value={form.alter_contact_name}
//               onChange={handleChange}
//             />

//             <Input
//               label="Alternate Contact Number"
//               name="alter_number"
//               value={form.alter_number}
//               onChange={handleChange}
//             />

//             <Select
//               label="Department"
//               value={form.department_id}
//               onChange={(e) =>
//                 setForm({ ...form, department_id: e.target.value })
//               }
//             >
//               <option value="">Select Department</option>
//               {departments.map((d) => (
//                 <option key={d.id} value={d.id}>
//                   {d.name}
//                 </option>
//               ))}
//             </Select>

//             <Input label="Job Title" name="job_title" value={form.job_title} onChange={handleChange} />
//             <Input type="date" label="DOB" name="dob" value={form.dob} onChange={handleChange} />
//             <Input label="Biometric ID" name="biometric_id" value={form.biometric_id} onChange={handleChange} />

//             <Textarea label="Address" name="address" value={form.address} onChange={handleChange} />

//             <Select label="Gender" name="gender" value={form.gender} onChange={handleChange}>
//               <option value="">Select Gender</option>
//               <option value="male">Male</option>
//               <option value="female">Female</option>
//             </Select>

//             {/* ACTIONS */}
//             <div className="flex justify-end gap-3 pt-4">
//               <button
//                 type="button"
//                 onClick={() => setShowResetModal(true)}
//                 className="px-4 py-2 rounded-lg bg-red-500/80 hover:bg-red-500"
//               >
//                 Reset Password
//               </button>

//               <button
//                 type="submit"
//                 className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-500"
//               >
//                 Update
//               </button>
//             </div>
//           </form>
//         </div>
//       </div>

//       {/* RESET PASSWORD */}
//       {showResetModal &&
//         createPortal(
//           <div className="fixed inset-0 bg-black/60 z-[3000] flex items-center justify-center">
//             <div className="bg-white rounded-2xl p-6 w-[320px]">
//               <h3 className="font-semibold mb-4">New Password</h3>
//               <input
//                 value={newPassword}
//                 onChange={(e) => setNewPassword(e.target.value)}
//                 className="w-full px-3 py-2 border rounded mb-4"
//               />
//               <div className="flex justify-end gap-3">
//                 <button onClick={() => setShowResetModal(false)}>Cancel</button>
//                 <button
//                   onClick={handleResetPassword}
//                   className="bg-indigo-600 text-white px-4 py-2 rounded"
//                 >
//                   Submit
//                 </button>
//               </div>
//             </div>
//           </div>,
//           document.body
//         )}

//       <UpdateResponseModal
//         show={updateModal.show}
//         success={updateModal.success}
//         message={updateModal.message}
//         onClose={() => {
//           setUpdateModal({ show: false, success: false, message: "" });
//           if (updateModal.success) onClose();
//         }}
//       />
//     </>,
//     document.body
//   );
// }

// /* ================= HELPERS ================= */

// function Input({ label, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <input
//         {...props}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//       />
//     </div>
//   );
// }

// function Select({ label, children, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <select
//         {...props}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//       >
//         {children}
//       </select>
//     </div>
//   );
// }

// function Textarea({ label, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <textarea
//         {...props}
//         rows={3}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 resize-none"
//       />
//     </div>
//   );
// }
